/**
 * Delivery of Possession (DOP) API Service
 * Handles all API calls for DOP functionality
 */

import axios from 'axios';

const API_BASE_URL = 'https://api.apps.rabahrain.com/api/crm/dop';
const API_KEY = 'V+kBloEwqqVho1PINmQI0VJQwSBX2l/e';

/**
 * Get common headers for all API requests
 * @param {string} token - Bearer token
 * @returns {Object} Headers object
 */
export const getCommonHeaders = (token) => ({
  'Content-Type': 'application/json',
  'accept': '*/*',
  'Authorization': `Bearer ${token}`,
  'apiKey': API_KEY,
  'db': 'ePMS1106',
  'COMPANY_NO': '1106',
});

/**
 * Get DOP listing with pagination, sorting, and filtering
 * @param {string} token - Bearer token
 * @param {Object} params - Query parameters {take, skip, sortColumn, sortDirection, search}
 * @returns {Promise} Response data
 */
export const getDOPListing = async (token, params = {}) => {
  try {
    const defaultParams = {
      take: params.take || 10,
      skip: params.skip || 0,
      sortColumn: params.sortColumn || 'ModifiedOn',
      sortDirection: params.sortDirection || 1,
      search: params.search || '',
    };

    console.log('DOP Listing Request:', {
      url: `${API_BASE_URL}/listing`,
      params: defaultParams,
      headers: getCommonHeaders(token),
    });

    const resp = await axios.post(`${API_BASE_URL}/listing`, defaultParams, {
      headers: getCommonHeaders(token),
    });

    console.log('DOP Listing Response:', resp.data.data.items);
    return resp.data.data.items;
  } catch (error) {
    console.error('Error fetching DOP listing:', error);
    throw error;
  }
};

/**
 * Get DOP dropdowns (Property, Unit, Reservation, etc.)
 * @param {string} token - Bearer token
 * @returns {Promise} Response data
 */
export const getDOPDropdowns = async (token) => {
  try {
    console.log('DOP Dropdowns Request:', {
      url: `${API_BASE_URL}/dropdowns`,
      headers: getCommonHeaders(token),
    });

    const resp = await axios.get(`${API_BASE_URL}/dropdowns`, {
      headers: getCommonHeaders(token),
    });

    console.log('DOP Dropdowns Response:', resp.data);
    return resp.data;
  } catch (error) {
    console.error('Error fetching DOP dropdowns:', error);
    throw error;
  }
};

/**
 * Get single DOP record by ID
 * @param {string} token - Bearer token
 * @param {string} id - DOP record ID
 * @returns {Promise} Response data
 */
export const getDOPById = async (token, id) => {
  try {
    console.log('Get DOP by ID Request:', {
      url: `${API_BASE_URL}/${id}`,
      headers: getCommonHeaders(token),
    });

    const resp = await axios.get(`${API_BASE_URL}/${id}`, {
      headers: getCommonHeaders(token),
    });

    console.log('Get DOP by ID Response:', resp.data);
    return resp.data;
  } catch (error) {
    console.error('Error fetching DOP record:', error);
    throw error;
  }
};

/**
 * Create or update DOP record
 * @param {string} token - Bearer token
 * @param {Object} dopData - DOP data object
 * @returns {Promise} Response data
 */
export const saveDOP = async (token, dopData) => {
  try {
    console.log('Save DOP Request:', {
      url: `${API_BASE_URL}`,
      method: 'POST',
      headers: getCommonHeaders(token),
      body: dopData,
    });

    const resp = await axios.post(`${API_BASE_URL}`, dopData, {
      headers: getCommonHeaders(token),
    });

    console.log('Save DOP Response:', resp.data);
    return resp.data;
  } catch (error) {
    console.error('Error saving DOP record:', error);
    throw error;
  }
};

/**
 * Update DOP record
 * @param {string} token - Bearer token
 * @param {Object} dopData - DOP data object with updates
 * @returns {Promise} Response data
 */
export const updateDOP = async (token, dopData) => {
  try {
    console.log('Update DOP Request:', {
      url: `${API_BASE_URL}`,
      method: 'PUT',
      headers: getCommonHeaders(token),
      body: dopData,
    });

    const resp = await axios.put(`${API_BASE_URL}`, dopData, {
      headers: getCommonHeaders(token),
    });

    console.log('Update DOP Response:', resp.data);
    return resp.data;
  } catch (error) {
    console.error('Error updating DOP record:', error);
    throw error;
  }
};

/**
 * Get DOP record for printing
 * @param {string} token - Bearer token
 * @param {string} id - DOP record ID
 * @returns {Promise} Response data (PDF or printable format)
 */
export const printDOP = async (token, id) => {
  try {
    console.log('Print DOP Request:', {
      url: `${API_BASE_URL}/print/${id}`,
      headers: getCommonHeaders(token),
    });

    const resp = await axios.get(`${API_BASE_URL}/print/${id}`, {
      headers: getCommonHeaders(token),
      responseType: 'blob',
    });

    console.log('Print DOP Response: Blob received');
    return resp.data;
  } catch (error) {
    console.error('Error printing DOP record:', error);
    throw error;
  }
};

/**
 * Get bearer token from environment variables or localStorage
 * @returns {string} Bearer token (without 'Bearer ' prefix)
 */
export const getBearerToken = () => {
  // Try to get from localStorage first
  const localToken = localStorage.getItem('bearerToken');
  if (localToken) {
    return localToken;
  }

  // Fallback to environment variable
  const envToken = process.env.REACT_APP_AUTHORIZATION;
  if (envToken) {
    // Remove 'Bearer ' prefix if it exists
    return envToken.replace('Bearer ', '').trim();
  }

  return '';
};

/**
 * Set bearer token in localStorage
 * @param {string} token - Bearer token
 */
export const setBearerToken = (token) => {
  localStorage.setItem('bearerToken', token);
};
